/**
 * Valinoshop — Advanced UX Enhancements v2
 * Quick View | Newsletter Popup | Size Guide | Recently Viewed |
 * Sticky ATC | Wishlist | Promo Bar | Back to Top | Product Badges |
 * Guarantee Strip | Sticky Header | Section Reveal | Cart Pulse
 */
(function () {
  'use strict';

  /* ============================================================
     SCROLL-JUMP KILL SWITCH (defensive last-resort)
     ------------------------------------------------------------
     If any code path slips past our preventScroll patches and the
     page jumps > 1200px instantly without a recent user input,
     revert it. Catches Dawn defaults / third-party widgets / future
     theme updates that reintroduce naked .focus()/scrollIntoView.
     ============================================================ */
  (function () {
    var lastUserInputAt = Date.now();
    var lastScrollY = window.scrollY;
    var lastScrollAt = Date.now();
    var INPUT_EVENTS = ['mousedown','touchstart','keydown','wheel'];
    function markInput() { lastUserInputAt = Date.now(); }
    for (var i = 0; i < INPUT_EVENTS.length; i++) {
      window.addEventListener(INPUT_EVENTS[i], markInput, { passive: true, capture: true });
    }
    /* Allow intentional smooth scrolls (back-to-top button, anchor links)
       by also marking input on any synchronous scrollTo call. */
    var origScrollTo = window.scrollTo.bind(window);
    window.scrollTo = function () {
      lastUserInputAt = Date.now();
      return origScrollTo.apply(window, arguments);
    };
    window.addEventListener('scroll', function () {
      var now = Date.now();
      var y = window.scrollY;
      var dy = y - lastScrollY;
      var dt = now - lastScrollAt;
      /* Conditions for "spontaneous big jump":
         - moved > 1200px
         - in < 50ms (faster than any normal user fling)
         - last user input was > 200ms ago (not part of an in-progress gesture)
         - browser supports the scrollTo restore */
      if (Math.abs(dy) > 1200 && dt < 50 && (now - lastUserInputAt) > 200) {
        origScrollTo({ top: lastScrollY, behavior: 'instant' });
        if (window.console && window.console.warn) {
          console.warn('[valino] spontaneous scroll jump reverted (dy=' + dy + 'px). Active element:', document.activeElement);
        }
        /* lastScrollY stays — we successfully reverted */
        return;
      }
      lastScrollY = y;
      lastScrollAt = now;
    }, { passive: true });
  })();

  /* ============================================================
     UTILS
  ============================================================ */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }
  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function safeImageUrl(u) {
    var s = String(u || '');
    if (!s) return '';
    if (s.charAt(0) === '/') return s;
    if (/^https?:\/\//i.test(s) || s.indexOf('//') === 0) return s;
    return '';
  }

  /* ------------------------------------------------------------------
     UNIFIED SCROLL LOCK  (window.valinoScrollLock)
     ------------------------------------------------------------------
     Problem we're solving: the theme historically had THREE independent
     scroll-lock mechanisms — this counter, cart-drawer.js's class toggle,
     and valino-mega-nav's direct style assignment. When two overlapped
     (e.g. cart drawer + quick view) closing one would stomp the other's
     state and leave the page unscrollable until a hard refresh. Users
     reported this as "the site freezes, I have to reload to scroll."

     This owner-keyed Set is now the SINGLE source of truth. Every caller
     passes a unique string owner id; the body stays locked as long as
     at least one owner is active. Double-lock with the same owner is a
     no-op (prevents imbalanced counters). Self-heal logic below catches
     orphaned locks where the owning element has already been hidden.
  ------------------------------------------------------------------ */
  var _lockOwners = Object.create(null);
  var _lockCount = 0;
  function _applyLockState() {
    if (_lockCount > 0) {
      if (document.body.style.overflow !== 'hidden') document.body.style.overflow = 'hidden';
    } else {
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
      /* Also clear iOS fixed-body trick if any caller used it */
      if (document.body.style.position === 'fixed') document.body.style.position = '';
    }
  }
  function lockBy(owner) {
    if (!owner) owner = 'anon-' + Math.random();
    if (_lockOwners[owner]) return;
    _lockOwners[owner] = true;
    _lockCount++;
    _applyLockState();
  }
  function unlockBy(owner) {
    if (!owner || !_lockOwners[owner]) return;
    delete _lockOwners[owner];
    _lockCount--;
    if (_lockCount < 0) _lockCount = 0;
    _applyLockState();
  }
  function resetLock() {
    _lockOwners = Object.create(null);
    _lockCount = 0;
    /* Also clear any classList-based lock added by other scripts that
       didn't route through this helper (cart-drawer, details-modal). */
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
    document.body.style.position = '';
  }

  /* Legacy helpers — always pass an explicit owner id so two simultaneous
     modals can't stomp each other's lock (root cause of the
     Quick-View-over-Newsletter freeze that users hit). */
  function bodyLock(name) { lockBy(name || 'legacy'); }
  function bodyUnlock(name) { unlockBy(name || 'legacy'); }

  /* Public global so cart-drawer.js, details-modal.js, valino-mega-nav
     and anything else can participate without reinventing. */
  window.valinoScrollLock = {
    lock: lockBy,
    unlock: unlockBy,
    reset: resetLock,
    isLocked: function () { return _lockCount > 0; }
  };

  /* Safety net 1: navigating away clears everything. */
  window.addEventListener('pagehide', resetLock);
  window.addEventListener('beforeunload', resetLock);

  /* Safety net 2: the big one. Every ~1s, verify that the body is only
     locked if at least one known modal is ACTUALLY visible. If scroll
     is locked but no modal is on-screen, something leaked — force
     unlock. This is what ultimately saves users whose scroll got stuck
     without them being able to find the thing blocking them. */
  var KNOWN_MODALS = [
    '#valino-quick-view.is-open',
    '#valino-newsletter-popup.is-open',
    '#valino-size-guide.is-open',
    '#valino-find-my-size.is-open',
    '.vmn__dr--on',                      /* mega-nav mobile drawer */
    'cart-drawer.active',
    'details-modal details[open]',
    'modal-dialog[open]',                /* Dawn template popups */
    'pickup-availability-drawer[open]',  /* PDP pickup */
    'dialog[open]',
    '.modal-opened',
    '.overlay-open'
  ].join(', ');
  function selfHealScrollLock() {
    /* If body is locked but no known overlay is active, release. Catches
       every "I need to refresh to scroll again" scenario we've seen. */
    var cl = document.body.classList;
    var bodyLocked = document.body.style.overflow === 'hidden' ||
                      cl.contains('overflow-hidden') ||
                      cl.contains('overflow-hidden-mobile') ||
                      cl.contains('overflow-hidden-tablet');
    if (!bodyLocked) return;
    var anyModal = document.querySelector(KNOWN_MODALS);
    if (!anyModal) {
      resetLock();
      cl.remove('overflow-hidden-mobile');
      cl.remove('overflow-hidden-tablet');
    }
  }
  setInterval(selfHealScrollLock, 1000);

  /* Safety net 3: Escape anywhere, any time — if user panics and hits
     escape, always give them their scroll back as a last resort. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    /* Yield to modal-specific escape handlers, then double-check. */
    setTimeout(selfHealScrollLock, 50);
  });

  /* Safety net 4: if the user clicks anywhere AFTER all modals closed,
     but scroll is still locked, release immediately. */
  document.addEventListener('click', function () {
    setTimeout(selfHealScrollLock, 0);
  }, { passive: true, capture: true });

  /* Single rAF-throttled scroll dispatcher. All internal modules subscribe via
     onScroll(fn); we run the callbacks on the next animation frame instead of
     each binding its own listener. This keeps the main thread responsive on
     long pages (the "frozen scroll" feel comes from too many listeners firing
     synchronously on every scroll tick). */
  var _scrollSubs = [];
  var _scrollTicking = false;
  function _scrollFlush() {
    _scrollTicking = false;
    var y = window.scrollY;
    for (var i = 0; i < _scrollSubs.length; i++) {
      try { _scrollSubs[i](y); } catch (e) {}
    }
  }
  function onScroll(fn) {
    _scrollSubs.push(fn);
  }
  window.addEventListener('scroll', function () {
    if (_scrollTicking) return;
    _scrollTicking = true;
    requestAnimationFrame(_scrollFlush);
  }, { passive: true });

  /* ============================================================
     1. PROMO / ANNOUNCEMENT BAR
     Brief 002R1 UI-01: the header previously carried two announcement
     surfaces — this JS-injected rotating bar and sections/valino-promo-bar
     .liquid's scrolling ticker. The ticker is the theme-native section
     (editable in Admin, contributes no unsupported dispatch/transit claims)
     so it now stands alone; this duplicate is removed rather than disabled,
     since nothing else in the file reads PROMOS/buildPromoBar.
  ============================================================ */

  /* ============================================================
     2. QUICK VIEW MODAL
  ============================================================ */
  function buildQuickViewModal() {
    if (document.getElementById('valino-quick-view')) return;
    var modal = document.createElement('div');
    modal.id = 'valino-quick-view';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Quick View');
    modal.innerHTML = [
      '<div class="vqv-backdrop"></div>',
      '<div class="vqv-container">',
      '  <button class="vqv-close" aria-label="Close">&times;</button>',
      '  <div class="vqv-body">',
      '    <div class="vqv-loading"><div class="vqv-spinner"></div></div>',
      '    <div class="vqv-content"></div>',
      '  </div>',
      '</div>'
    ].join('');
    document.body.appendChild(modal);
    modal.querySelector('.vqv-backdrop').addEventListener('click', closeQuickView);
    modal.querySelector('.vqv-close').addEventListener('click', closeQuickView);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeQuickView(); closeSizeGuide(); closeFindMySize(); }
    });
  }

  function openQuickView(handle) {
    buildQuickViewModal();
    var modal = document.getElementById('valino-quick-view');
    var content = modal.querySelector('.vqv-content');
    var loading = modal.querySelector('.vqv-loading');
    content.innerHTML = '';
    loading.style.display = 'flex';
    modal.classList.add('is-open');
    bodyLock('quick-view');

    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        loading.style.display = 'none';
        var img = product.featured_image || (product.images && product.images[0] && product.images[0]) || '';
        var comparePrice = product.compare_at_price ? money(product.compare_at_price) : null;
        var priceHtml = comparePrice
          ? '<span class="vqv-price sale">' + money(product.price) + '</span> <s class="vqv-compare">' + comparePrice + '</s>'
          : '<span class="vqv-price">' + money(product.price) + '</span>';
        var productUrl = '/products/' + product.handle;
        var available = product.available;
        var desc = (product.description || '').replace(/<[^>]*>/g, '').trim().slice(0, 200);
        if (desc.length === 200) desc += '…';

        // Build variant selector
        var variantHtml = '';
        var sizeOption = null;
        if (product.options && product.options.length > 0) {
          product.options.forEach(function (opt) {
            var lower = opt.name.toLowerCase();
            if (lower === 'size' || lower === 'taille') sizeOption = opt;
          });
        }
        if (sizeOption) {
          variantHtml = '<div class="vqv-sizes"><strong style="font-size:12px;letter-spacing:.05em;text-transform:uppercase;">Size</strong><div class="vqv-size-opts">';
          sizeOption.values.forEach(function (v) {
            variantHtml += '<button class="vqv-size-opt" data-val="' + v + '">' + v + '</button>';
          });
          variantHtml += '</div></div>';
        }

        var safeImg = safeImageUrl(img);
        var safeTitle = escapeHtml(product.title || '');
        var safeVendor = escapeHtml(product.vendor || '');
        var safeDesc = escapeHtml(desc);
        var safeUrl = escapeHtml(productUrl);
        content.innerHTML = [
          '<div class="vqv-image-wrap">',
          safeImg ? '<img src="' + escapeHtml(safeImg) + '" alt="' + safeTitle + '" loading="eager" decoding="async">' : '',
          '</div>',
          '<div class="vqv-info">',
          '  <p class="vqv-vendor">' + safeVendor + '</p>',
          '  <h2 class="vqv-title">' + safeTitle + '</h2>',
          '  <div class="vqv-pricing">' + priceHtml + '</div>',
          '  <p class="vqv-desc">' + safeDesc + '</p>',
          variantHtml,
          '  <div class="vqv-actions">',
          available
            ? '<a class="vqv-atc button" href="' + safeUrl + '">View Full Details</a>'
            : '<button class="vqv-atc button" disabled>Sold Out</button>',
          '    <a class="vqv-link" href="' + safeUrl + '">See all photos &amp; options &rarr;</a>',
          '  </div>',
          '  <div class="vqv-trust">',
          '    <span>&#10003; Free Returns</span>',
          '    <span>&#128274; Secure Payment</span>',
          '    <span>&#128666; Fast Shipping</span>',
          '  </div>',
          '</div>'
        ].join('');

        content.querySelectorAll('.vqv-size-opt').forEach(function (btn) {
          btn.addEventListener('click', function () {
            content.querySelectorAll('.vqv-size-opt').forEach(function (b) { b.classList.remove('active'); });
            btn.classList.add('active');
          });
        });
      })
      .catch(function () {
        loading.style.display = 'none';
        content.innerHTML = '<p style="padding:24px;text-align:center">Unable to load product. <a href="/products/' + handle + '">View page</a></p>';
        /* Do NOT leave scroll locked if user closes this by tapping the
           backdrop after an error — close handlers already call bodyUnlock,
           but if the modal hides itself on navigation the lock leaks.
           Auto-release after 3s so a stalled fetch never freezes scroll. */
        setTimeout(function () {
          var m = document.getElementById('valino-quick-view');
          if (m && !m.classList.contains('is-open')) bodyUnlock('quick-view');
        }, 3000);
      });
  }

  function closeQuickView() {
    var modal = document.getElementById('valino-quick-view');
    if (modal) {
      modal.classList.remove('is-open');
      bodyUnlock('quick-view');
    }
  }


  /* ============================================================
     3. BACK TO TOP BUTTON
  ============================================================ */
  function buildBackToTop() {
    if (document.getElementById('valino-back-top')) return;
    var btn = document.createElement('button');
    btn.id = 'valino-back-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg>';
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    document.body.appendChild(btn);
    onScroll(function (y) {
      btn.classList.toggle('is-visible', y > 400);
    });
  }

  /* ============================================================
     4. GUARANTEE STRIP ON PRODUCT PAGES
  ============================================================ */
  function injectGuaranteeStrip() {
    if (qs('.valino-product-guarantee')) return;
    var atcBtn = qs('.product-form__submit');
    if (!atcBtn) return;
    var strip = document.createElement('div');
    strip.className = 'valino-product-guarantee';
    strip.innerHTML = [
      '<span>&#10003;&nbsp;30-Day Returns</span>',
      '<span>&#128274;&nbsp;Secure Checkout</span>',
      '<span>&#128666;&nbsp;Ships in 5–12 business days</span>',
      '<span>&#9733;&nbsp;Tracked Delivery</span>'
    ].join('');
    var form = atcBtn.closest('form');
    if (form) form.after(strip); else atcBtn.parentNode.insertBefore(strip, atcBtn.nextSibling);
  }

  /* ============================================================
     5. STICKY HEADER SHRINK ON SCROLL
  ============================================================ */
  function initStickyHeader() {
    var header = qs('.header, header.shopify-section');
    if (!header) return;
    var lastState = false;
    onScroll(function (y) {
      var nextState = y > 60;
      if (nextState !== lastState) {
        lastState = nextState;
        header.classList.toggle('valino-header-scrolled', nextState);
      }
    });
  }

  /* ============================================================
     6. CART NOTIFICATION BADGE PULSE
  ============================================================ */
  function initCartPulse() {
    document.addEventListener('cart:add', function () {
      var badge = qs('.cart-count-bubble');
      if (badge) {
        badge.classList.add('valino-pulse');
        setTimeout(function () { badge.classList.remove('valino-pulse'); }, 600);
      }
    });
  }

  /* ============================================================
     7. LAZY LOAD IMPROVEMENTS
  ============================================================ */
  function lazyLoadImages() {
    if (!('IntersectionObserver' in window)) return;
    var imgs = qsa('img[loading="lazy"]:not([data-valino-lazy])');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('loading', 'eager');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px' });
    imgs.forEach(function (img) {
      img.setAttribute('data-valino-lazy', '1');
      io.observe(img);
    });
  }

  /* ============================================================
     8. SMOOTH SECTION REVEAL ON SCROLL
  ============================================================ */
  function initSectionReveal() {
    if (!('IntersectionObserver' in window)) return;
    var sections = qsa('.shopify-section:not(.valino-revealed)');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('valino-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });
    sections.forEach(function (s) { io.observe(s); });
  }

  /* ============================================================
     9. NEWSLETTER POPUP
  ============================================================ */
  function buildNewsletterPopup() {
    if (localStorage.getItem('valino_nl_done')) return;
    if (document.getElementById('valino-newsletter-popup')) return;
    /* Smart gating — do not interrupt transactional flows or loyal customers */
    var path = window.location.pathname || '';
    if (/\/cart|\/checkouts?|\/account|\/apps|\/challenge/.test(path)) return;
    /* Skip if Shopify body tag says customer has logged in and ordered */
    var bodyCls = (document.body && document.body.className) || '';
    if (/customer-logged-in/.test(bodyCls) && localStorage.getItem('valino_nl_snooze_loggedin') !== '1') {
      /* Snooze for logged-in users after first auto-skip so they never see it */
      try { localStorage.setItem('valino_nl_snooze_loggedin', '1'); } catch (e) {}
      return;
    }

    var modal = document.createElement('div');
    modal.id = 'valino-newsletter-popup';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Newsletter signup');
    modal.innerHTML = [
      '<div class="vnl-backdrop"></div>',
      '<div class="vnl-container">',
      '  <div class="vnl-art">',
      '    <div class="vnl-art-icon">👗</div>',
      '    <div class="vnl-art-label">Exclusive Offer</div>',
      '    <div class="vnl-art-big">10%<br>OFF</div>',
      '    <div class="vnl-art-sub">Your First Order</div>',
      '  </div>',
      '  <div class="vnl-body">',
      '    <button class="vnl-close" aria-label="Close">&times;</button>',
      '    <div class="vnl-eyebrow">Welcome to Valino</div>',
      '    <h2 class="vnl-title">Get 10% Off Your First Order</h2>',
      '    <p class="vnl-desc">Join thousands of style lovers. Get early access to sales, new arrivals, and exclusive deals delivered to your inbox.</p>',
      '    <div class="vnl-form-wrap">',
      '      <form class="vnl-form" action="/contact#contact_form" method="post" accept-charset="UTF-8" novalidate>',
      '        <input type="hidden" name="form_type" value="customer">',
      '        <input type="hidden" name="utf8" value="&#x2713;">',
      '        <input type="hidden" name="contact[tags]" value="newsletter, valino-popup, WELCOME10">',
      '        <input type="hidden" name="contact[accepts_marketing]" value="yes">',
      '        <input type="email" class="vnl-input" name="contact[email]" placeholder="Your email address" autocomplete="email" required>',
      '        <input type="tel" class="vnl-input vnl-input-phone" name="contact[phone]" placeholder="Phone (optional — get SMS alerts on drops + restocks)" autocomplete="tel" inputmode="tel">',
      '        <input type="hidden" name="contact[sms_marketing_consent]" value="" data-vnl-sms-consent>',
      '        <div class="vnl-error" data-vnl-error style="display:none;"></div>',
      '        <button type="submit" class="vnl-submit">Claim My 10% Off &rarr;</button>',
      '      </form>',
      '      <p class="vnl-skip">No thanks, I prefer paying full price</p>',
      '    </div>',
      '    <div class="vnl-success">',
      '      <div class="vnl-success-icon">🎉</div>',
      '      <div class="vnl-success-title">You\'re in!</div>',
      '      <p style="font-size:13px;color:#666;margin-bottom:8px;">Use this code at checkout:</p>',
      '      <div class="vnl-success-code">WELCOME10</div>',
      '      <p style="font-size:12px;color:#999;margin-top:8px;">Check your inbox for your discount email</p>',
      '    </div>',
      '  </div>',
      '</div>'
    ].join('');
    document.body.appendChild(modal);

    function closeNewsletter() {
      modal.classList.remove('is-open');
      bodyUnlock('newsletter');
      localStorage.setItem('valino_nl_done', '1');
    }

    function openNewsletter() {
      modal.classList.add('is-open');
      bodyLock('newsletter');
    }

    modal.querySelector('.vnl-backdrop').addEventListener('click', closeNewsletter);
    modal.querySelector('.vnl-close').addEventListener('click', closeNewsletter);
    modal.querySelector('.vnl-skip').addEventListener('click', closeNewsletter);

    // Phone validation regex — international format, allows +, digits, spaces, dashes, parens; requires 7-15 digits total.
    function isValidPhone(v) {
      var digits = (v || '').replace(/[^\d]/g, '');
      if (digits.length < 7 || digits.length > 15) return false;
      return /^\+?[\d\s().\-]{7,}$/.test(v);
    }
    function normalizePhone(v) {
      var s = (v || '').trim();
      if (!s) return '';
      var digits = s.replace(/[^\d]/g, '');
      if (s.charAt(0) === '+') return '+' + digits;
      // Default to US (+1) if 10 digits, else prefix + on whatever we have
      if (digits.length === 10) return '+1' + digits;
      return '+' + digits;
    }

    var form = modal.querySelector('.vnl-form');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var emailInput = modal.querySelector('input[name="contact[email]"]');
      var phoneInput = modal.querySelector('input[name="contact[phone]"]');
      var smsConsent = modal.querySelector('[data-vnl-sms-consent]');
      var errorBox = modal.querySelector('[data-vnl-error]');
      var email = emailInput.value.trim();
      var phoneRaw = phoneInput.value.trim();

      // Reset error state
      emailInput.style.borderColor = '';
      phoneInput.style.borderColor = '';
      errorBox.style.display = 'none';
      errorBox.textContent = '';

      // Email required
      if (!email || !email.includes('@')) {
        emailInput.style.borderColor = '#e53935';
        emailInput.focus({ preventScroll: true });
        errorBox.textContent = 'Please enter a valid email address.';
        errorBox.style.display = 'block';
        return;
      }

      // Phone optional — validate only if provided
      if (phoneRaw) {
        if (!isValidPhone(phoneRaw)) {
          phoneInput.style.borderColor = '#e53935';
          errorBox.textContent = 'Please enter a valid phone number (include country code, e.g. +1 555 123 4567). Leave blank to skip SMS.';
          errorBox.style.display = 'block';
          phoneInput.focus({ preventScroll: true });
          return;
        }
        // Normalize to international format and set SMS consent
        phoneInput.value = normalizePhone(phoneRaw);
        smsConsent.value = 'yes';
      } else {
        // No phone — strip name so Shopify doesn't attempt to record an empty phone
        phoneInput.removeAttribute('name');
        smsConsent.removeAttribute('name');
      }

      // Submit to Shopify's native customer form endpoint via fetch
      var fd = new FormData(form);
      fetch('/contact', { method: 'POST', body: fd, headers: { 'Accept': 'text/html' }, credentials: 'same-origin' })
        .catch(function () { /* swallow — still show success client-side */ });

      // Show success state
      modal.querySelector('.vnl-form-wrap').style.display = 'none';
      modal.querySelector('.vnl-success').style.display = 'block';
      localStorage.setItem('valino_nl_done', '1');
      // Auto close after 4 seconds
      setTimeout(closeNewsletter, 4000);
    });

    // Open after 12 seconds of engagement, exit-intent on desktop,
    // or mobile "rapid scroll-to-top" (proxy for pull-to-refresh / back tap)
    var opened = false;
    function triggerOnce() {
      if (!opened) { opened = true; openNewsletter(); }
    }
    setTimeout(triggerOnce, 12000);

    // Exit intent on desktop
    document.addEventListener('mouseleave', function (e) {
      if (e.clientY <= 0) triggerOnce();
    });

    // Mobile: detect fast upward scroll near top of page
    var lastY = window.scrollY, lastT = Date.now();
    onScroll(function (y) {
      var t = Date.now();
      var dy = y - lastY, dt = t - lastT;
      if (dt > 0 && dy < -80 && y < 140 && dt < 260) triggerOnce();
      lastY = y; lastT = t;
    });
  }

  /* ============================================================
     10. SIZE GUIDE MODAL
  ============================================================ */
  function buildSizeGuideModal() {
    if (document.getElementById('valino-size-guide')) return;
    var modal = document.createElement('div');
    modal.id = 'valino-size-guide';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Size Guide');

    var womensRows = [
      ['XS', '0-2', '32"', '24"', '34"', 'UK 6'],
      ['S',  '4-6', '34"', '26"', '36"', 'UK 8'],
      ['M',  '8-10','36"', '28"', '38"', 'UK 10-12'],
      ['L',  '12-14','38"', '30"', '40"', 'UK 14-16'],
      ['XL', '16-18','40"', '32"', '42"', 'UK 18'],
      ['XXL','20-22','42"', '34"', '44"', 'UK 20-22']
    ];
    var mensRows = [
      ['XS', '32-34', '28-30', '32-34', 'EU 44'],
      ['S',  '34-36', '30-32', '34-36', 'EU 46'],
      ['M',  '36-38', '32-34', '36-38', 'EU 48-50'],
      ['L',  '38-40', '34-36', '38-40', 'EU 52-54'],
      ['XL', '40-42', '36-38', '40-42', 'EU 56'],
      ['XXL','42-44', '38-40', '42-44', 'EU 58']
    ];
    var shoesRows = [
      ['5',   '35-36', '22.0 cm', '8.5"'],
      ['6',   '36-37', '23.0 cm', '9.1"'],
      ['7',   '37-38', '23.5 cm', '9.3"'],
      ['8',   '38-39', '24.0 cm', '9.5"'],
      ['9',   '39-40', '25.0 cm', '9.8"'],
      ['10',  '40-41', '25.5 cm', '10"'],
      ['11',  '41-42', '26.0 cm', '10.3"'],
      ['12',  '42-43', '27.0 cm', '10.6"']
    ];

    function buildTable(headers, rows) {
      var html = '<div class="vsg-table-wrap"><table class="vsg-table"><thead><tr>';
      headers.forEach(function (h) { html += '<th>' + h + '</th>'; });
      html += '</tr></thead><tbody>';
      rows.forEach(function (row) {
        html += '<tr>';
        row.forEach(function (cell) { html += '<td>' + cell + '</td>'; });
        html += '</tr>';
      });
      html += '</tbody></table></div>';
      return html;
    }

    modal.innerHTML = [
      '<div class="vsg-backdrop"></div>',
      '<div class="vsg-container">',
      '  <div class="vsg-header">',
      '    <span class="vsg-title">📏 Size Guide</span>',
      '    <button class="vsg-close" aria-label="Close">&times;</button>',
      '  </div>',
      '  <div class="vsg-tabs">',
      '    <button class="vsg-tab active" data-panel="women">Women</button>',
      '    <button class="vsg-tab" data-panel="men">Men</button>',
      '    <button class="vsg-tab" data-panel="shoes">Shoes</button>',
      '  </div>',
      '  <div class="vsg-body">',
      '    <div class="vsg-panel active" id="vsg-panel-women">',
      buildTable(['Size', 'US', 'Bust', 'Waist', 'Hips', 'UK'], womensRows),
      '      <div class="vsg-hint"><strong>How to measure:</strong> Bust — measure around the fullest part. Waist — measure around the narrowest part. Hips — measure around the fullest part of your hips. Add 1-2" for comfort.</div>',
      '    </div>',
      '    <div class="vsg-panel" id="vsg-panel-men">',
      buildTable(["Size", "Chest", "Waist", "Hips", "EU"], mensRows),
      '      <div class="vsg-hint"><strong>Tip:</strong> For a relaxed fit, choose one size up. All measurements are body measurements in inches unless noted.</div>',
      '    </div>',
      '    <div class="vsg-panel" id="vsg-panel-shoes">',
      buildTable(["US", "EU", "Foot Length", "Inches"], shoesRows),
      '      <div class="vsg-hint"><strong>Tip:</strong> Measure your foot length in the afternoon when feet are slightly larger. Choose the larger size if between sizes.</div>',
      '    </div>',
      '  </div>',
      '</div>'
    ].join('');

    document.body.appendChild(modal);

    modal.querySelector('.vsg-backdrop').addEventListener('click', closeSizeGuide);
    modal.querySelector('.vsg-close').addEventListener('click', closeSizeGuide);
    modal.querySelectorAll('.vsg-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        modal.querySelectorAll('.vsg-tab').forEach(function (t) { t.classList.remove('active'); });
        modal.querySelectorAll('.vsg-panel').forEach(function (p) { p.classList.remove('active'); });
        tab.classList.add('active');
        var panel = document.getElementById('vsg-panel-' + tab.dataset.panel);
        if (panel) panel.classList.add('active');
      });
    });
  }

  function openSizeGuide() {
    buildSizeGuideModal();
    var modal = document.getElementById('valino-size-guide');
    if (modal) {
      modal.classList.add('is-open');
      bodyLock('size-guide');
    }
  }

  function closeSizeGuide() {
    var modal = document.getElementById('valino-size-guide');
    if (modal) {
      modal.classList.remove('is-open');
      bodyUnlock('size-guide');
    }
  }

  function injectSizeGuideButton() {
    if (qs('.valino-size-guide-btn')) return;
    // Look for size selector on product page
    var sizeLabel = null;
    qsa('.product-form__label, label').forEach(function (lbl) {
      if (!sizeLabel && /size|taille/i.test(lbl.textContent)) sizeLabel = lbl;
    });
    var target = sizeLabel || qs('.product-form__input--pill, .product-form__input');
    if (!target) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'valino-size-guide-btn';
    btn.innerHTML = '📏 Size Guide';
    btn.addEventListener('click', openSizeGuide);

    var parent = target.closest('.product-form__controls-group, .product-form__input-wrapper, .product-form');
    if (parent) parent.appendChild(btn);
    else target.parentNode.insertBefore(btn, target.nextSibling);
  }

  /* ============================================================
     15. FIND MY SIZE RECOMMENDER
     Calculates recommended clothing or shoe size from height + weight.
     Uses BMI-based formula for clothing, foot-length estimation for shoes.
     No external API — 100% local math.
  ============================================================ */

  function getClothingSize(heightCm, weightKg, gender) {
    var bmi  = weightKg / Math.pow(heightCm / 100, 2);
    var hAdj = heightCm > 175 ? 1.0 : (heightCm < 160 ? -0.8 : 0);
    var score = bmi + hAdj;
    var sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
    var thresh = gender === 'men'
      ? [19.5, 22.0, 25.0, 28.0, 31.0]
      : [18.5, 21.0, 23.5, 26.5, 30.0];
    var idx = 0;
    for (var i = 0; i < thresh.length; i++) { if (score >= thresh[i]) idx = i + 1; }
    return {
      size: sizes[idx],
      note: 'Based on your measurements. If between sizes, size up for a relaxed fit.'
    };
  }

  function getShoeSize(heightCm, gender) {
    /* Foot length ≈ height × 0.153 — established anthropometric ratio */
    var foot = heightCm * 0.153;
    var womenTable = [
      [21.6,'US 5 / EU 35'],[22.2,'US 5.5 / EU 35-36'],[22.8,'US 6 / EU 36-37'],
      [23.4,'US 6.5 / EU 37'],[24.0,'US 7 / EU 37-38'],[24.6,'US 7.5 / EU 38'],
      [25.2,'US 8 / EU 38-39'],[25.8,'US 8.5 / EU 39'],[26.4,'US 9 / EU 39-40'],
      [27.0,'US 9.5 / EU 40'],[27.6,'US 10 / EU 40-41']
    ];
    var menTable = [
      [24.4,'US 6.5 / EU 39'],[25.1,'US 7 / EU 40'],[25.7,'US 7.5 / EU 40-41'],
      [26.2,'US 8 / EU 41'],[26.8,'US 8.5 / EU 41-42'],[27.3,'US 9 / EU 42'],
      [27.9,'US 9.5 / EU 42-43'],[28.4,'US 10 / EU 43'],[29.0,'US 10.5 / EU 44'],
      [29.6,'US 11 / EU 44-45'],[30.2,'US 12 / EU 45-46']
    ];
    var table = gender === 'men' ? menTable : womenTable;
    var size = table[table.length - 1][1];
    for (var j = 0; j < table.length; j++) {
      if (foot <= table[j][0]) { size = table[j][1]; break; }
    }
    return {
      size: size,
      note: 'Est. foot length ' + foot.toFixed(1) + ' cm. For wide feet, size up half a size.'
    };
  }

  function getKidsSize(heightCm) {
    var table = [
      [86,'1-2Y'],[92,'2-3Y'],[98,'3-4Y'],[104,'4-5Y'],
      [110,'5-6Y'],[116,'6-7Y'],[122,'7-8Y'],[128,'8-9Y'],
      [134,'9-10Y'],[140,'10-11Y'],[146,'11-12Y'],[152,'12-13Y']
    ];
    var size = '13Y+';
    for (var k = 0; k < table.length; k++) {
      if (heightCm <= table[k][0]) { size = table[k][1]; break; }
    }
    return {
      size: size,
      note: 'Based on height ' + Math.round(heightCm) + ' cm. Check waist & chest on the product page for best fit.'
    };
  }

  function buildFindMySizeModal() {
    if (document.getElementById('valino-find-my-size')) return;
    var modal = document.createElement('div');
    modal.id = 'valino-find-my-size';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Find My Size');
    modal.innerHTML = [
      '<div class="vsmr__backdrop"></div>',
      '<div class="vsmr__container">',
      '<div class="vsmr__header">',
      '<span class="vsmr__title">&#128208; Find My Size</span>',
      '<button type="button" class="vsmr__close" aria-label="Close">&times;</button>',
      '</div>',
      '<div class="vsmr__body">',
      '<div class="vsmr__group">',
      '<span class="vsmr__label">I\'m shopping for</span>',
      '<div class="vsmr__pills" id="vsmr-gender">',
      '<button type="button" class="vsmr__pill vsmr__pill--active" data-gender="women">Women</button>',
      '<button type="button" class="vsmr__pill" data-gender="men">Men</button>',
      '<button type="button" class="vsmr__pill" data-gender="kids">Kids</button>',
      '</div></div>',
      '<div class="vsmr__row">',
      '<div class="vsmr__group">',
      '<label class="vsmr__label" for="vsmr-height">Height</label>',
      '<div class="vsmr__input-wrap">',
      '<input id="vsmr-height" type="number" class="vsmr__input" placeholder="170" min="60" max="230">',
      '<span class="vsmr__unit" id="vsmr-hunit">cm</span>',
      '</div></div>',
      '<div class="vsmr__group" id="vsmr-wgroup">',
      '<label class="vsmr__label" for="vsmr-weight">Weight</label>',
      '<div class="vsmr__input-wrap">',
      '<input id="vsmr-weight" type="number" class="vsmr__input" placeholder="65" min="20" max="300">',
      '<span class="vsmr__unit" id="vsmr-wunit">kg</span>',
      '</div></div></div>',
      '<div class="vsmr__unit-toggle">',
      '<button type="button" class="vsmr__toggle-btn vsmr__toggle-btn--active" id="vsmr-metric">Metric (cm / kg)</button>',
      '<button type="button" class="vsmr__toggle-btn" id="vsmr-imperial">Imperial (ft / lbs)</button>',
      '</div>',
      '<button type="button" class="vsmr__calc-btn" id="vsmr-calc">Find My Size &rarr;</button>',
      '<div class="vsmr__result" id="vsmr-result" style="display:none">',
      '<div class="vsmr__result-size" id="vsmr-size-out"></div>',
      '<div class="vsmr__result-note" id="vsmr-note-out"></div>',
      '</div>',
      '</div></div>'
    ].join('');
    document.body.appendChild(modal);

    modal.querySelector('.vsmr__backdrop').addEventListener('click', closeFindMySize);
    modal.querySelector('.vsmr__close').addEventListener('click', closeFindMySize);

    /* Gender pills */
    var pills = modal.querySelectorAll('.vsmr__pill');
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        pills.forEach(function (p) { p.classList.remove('vsmr__pill--active'); });
        pill.classList.add('vsmr__pill--active');
        var wg = document.getElementById('vsmr-wgroup');
        if (wg) wg.style.display = pill.getAttribute('data-gender') === 'kids' ? 'none' : '';
        var res = document.getElementById('vsmr-result');
        if (res) res.style.display = 'none';
      });
    });

    /* Unit toggle */
    var isMetric = true;
    function setUnits(metric) {
      isMetric = metric;
      document.getElementById('vsmr-metric').classList.toggle('vsmr__toggle-btn--active', metric);
      document.getElementById('vsmr-imperial').classList.toggle('vsmr__toggle-btn--active', !metric);
      document.getElementById('vsmr-height').placeholder = metric ? '170' : '5.7';
      document.getElementById('vsmr-weight').placeholder = metric ? '65' : '145';
      document.getElementById('vsmr-hunit').textContent   = metric ? 'cm' : 'ft';
      document.getElementById('vsmr-wunit').textContent   = metric ? 'kg' : 'lbs';
      document.getElementById('vsmr-height').value = '';
      document.getElementById('vsmr-weight').value = '';
      var res = document.getElementById('vsmr-result');
      if (res) res.style.display = 'none';
    }
    document.getElementById('vsmr-metric').addEventListener('click', function () { setUnits(true); });
    document.getElementById('vsmr-imperial').addEventListener('click', function () { setUnits(false); });

    /* Calculate */
    document.getElementById('vsmr-calc').addEventListener('click', function () {
      var hVal = parseFloat(document.getElementById('vsmr-height').value);
      var wVal = parseFloat(document.getElementById('vsmr-weight').value);
      var gender = (modal.querySelector('.vsmr__pill--active') || {}).getAttribute('data-gender') || 'women';

      if (!hVal || isNaN(hVal)) { alert('Please enter your height.'); return; }
      if (gender !== 'kids' && (!wVal || isNaN(wVal))) { alert('Please enter your weight.'); return; }

      var hCm = isMetric ? hVal : hVal * 30.48;
      var wKg = isMetric ? wVal : wVal * 0.4536;

      var _piEl1 = qs('product-info');
      var productType = (_piEl1 ? _piEl1.getAttribute('data-product-type') : '').toLowerCase();
      var isShoe = /shoe|boot|heel|sandal|flat|slipper|sneaker|footwear/i.test(productType);

      var result;
      if (isShoe) {
        result = getShoeSize(hCm, gender);
      } else if (gender === 'kids') {
        result = getKidsSize(hCm);
      } else {
        result = getClothingSize(hCm, wKg, gender);
      }

      document.getElementById('vsmr-size-out').textContent = result.size;
      document.getElementById('vsmr-note-out').textContent = result.note;
      document.getElementById('vsmr-result').style.display = 'block';
    });
  }

  function openFindMySize() {
    buildFindMySizeModal();
    var modal = document.getElementById('valino-find-my-size');
    if (!modal) return;
    /* Auto-detect gender from product type */
    var _piEl2 = qs('product-info');
    var pt = (_piEl2 ? _piEl2.getAttribute('data-product-type') : '').toLowerCase();
    if (/\bmen\b|male|boy/i.test(pt)) {
      var mp = modal.querySelector('[data-gender="men"]');
      if (mp) mp.click();
    } else if (/kid|child|baby|toddler|girl/i.test(pt)) {
      var kp = modal.querySelector('[data-gender="kids"]');
      if (kp) kp.click();
    }
    modal.classList.add('is-open');
    bodyLock('find-my-size');
  }

  function closeFindMySize() {
    var modal = document.getElementById('valino-find-my-size');
    if (modal) { modal.classList.remove('is-open'); bodyUnlock('find-my-size'); }
  }

  function injectFindMySizeButton() {
    if (qs('.valino-find-size-btn')) return;
    /* Only show for clothing / footwear product types */
    var _piEl3 = qs('product-info');
    var pt = (_piEl3 ? _piEl3.getAttribute('data-product-type') : '').toLowerCase();
    var RELEVANT = /shirt|blouse|dress|skirt|pant|jean|jacket|coat|sweater|hoodie|top|short|legging|active|sport|swim|linger|underwear|robe|suit|blazer|shoe|boot|heel|sandal|flat|slipper|sneaker|footwear|kid|child|baby|cloth/i;
    if (pt && !RELEVANT.test(pt)) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'valino-find-size-btn';
    btn.innerHTML = '&#128208; Find My Size';
    btn.addEventListener('click', openFindMySize);

    var sizeGuideBtn = qs('.valino-size-guide-btn');
    if (sizeGuideBtn && sizeGuideBtn.parentNode) {
      sizeGuideBtn.parentNode.insertBefore(btn, sizeGuideBtn.nextSibling);
      return;
    }
    /* Fallback: place near size selector */
    var sizeLabel = null;
    qsa('.product-form__label, label').forEach(function (lbl) {
      if (!sizeLabel && /size|taille/i.test(lbl.textContent)) sizeLabel = lbl;
    });
    var target = sizeLabel || qs('.product-form__input--pill, .product-form__input');
    if (!target) return;
    var parent = target.closest('.product-form__controls-group, .product-form__input-wrapper, .product-form');
    if (parent) parent.appendChild(btn);
    else target.parentNode.insertBefore(btn, target.nextSibling);
  }

  // Recently viewed is owned by sections/valino-recently-viewed.liquid.
  // It records the Liquid product handle and reads current product data.
  // Do not revive the legacy valino_rv DOM-scraping cache: collection grids
  // matched its product-page selector and contaminated titles and prices.

  /* ============================================================
     12. STICKY ADD TO CART (MOBILE)
  ============================================================ */
  function initStickyATC() {
    var atcBtn = qs('.product-form__submit');
    if (!atcBtn) return;

    var bar = document.createElement('div');
    bar.id = 'valino-sticky-atc';
    bar.className = 'is-hidden';

    var title = qs('.product__title h1, .product-single__title, h1.title');
    var priceEl = qs('.price__regular .price-item, .product__price .price-item, .price-item--regular');
    var imgEl = qs('.product__media img, .product-single__photo img');

    var imgSrc = imgEl ? safeImageUrl(imgEl.src) : '';
    var titleText = title ? title.textContent.trim().slice(0, 40) : 'Product';
    var priceText = priceEl ? priceEl.textContent.trim() : '';
    bar.innerHTML = (imgSrc ? '<img class="vsatc-img" src="' + escapeHtml(imgSrc) + '" alt="" decoding="async">' : '')
      + '<div class="vsatc-info">'
      + '<div class="vsatc-title">' + escapeHtml(titleText) + '</div>'
      + '<div class="vsatc-price">' + escapeHtml(priceText) + '</div>'
      + '</div>'
      + '<button class="vsatc-btn" type="button">Add to Cart</button>';

    document.body.appendChild(bar);

    bar.querySelector('.vsatc-btn').addEventListener('click', function () {
      atcBtn.click();
      bar.querySelector('.vsatc-btn').textContent = 'Adding…';
      setTimeout(function () { bar.querySelector('.vsatc-btn').textContent = 'Add to Cart'; }, 1500);
    });

    // Show when original ATC is scrolled out of view
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-hidden', entry.isIntersecting);
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    });
    io.observe(atcBtn);
  }

  // Card badges are rendered by Liquid from product availability, price
  // and configured merchandising data. Hidden sale markup and grid position
  // are not evidence for an additional SALE or NEW badge.

  /* ============================================================
     INIT
  ============================================================ */
  function init() {
    buildBackToTop();
    initStickyHeader();
    initCartPulse();
    lazyLoadImages();
    initSectionReveal();
    // Newsletter popup is now owned by sections/valino-email-popup.liquid (styled + scoped).
    // buildNewsletterPopup() below has no matching CSS and would render unstyled inline.
    // buildNewsletterPopup();

    // Product page specific
    if (/\/products\/[^/?#]+\/?$/.test(location.pathname) && qs('#MainContent [id^="MainProduct-"]')) {
      injectGuaranteeStrip();
      injectSizeGuideButton();
      injectFindMySizeButton();
      initStickyATC();
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest && !e.target.closest('.vmn__item--has-mega')) {
        qsa('.vmn__item--has-mega.vmn__item--open').forEach(function (it) {
          it.classList.remove('vmn__item--open');
        });
      }
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   WAVE 1  ·  CONVERSION & TRUST ENHANCERS
   Additive only; safe to delete this banner to revert.
   ============================================================ */

/* Step 5 — Variant pill color-dot enhancer.
   If an option group's legend matches /color|colour|finish|shade|metal|tone/i,
   inject a color dot inside each pill label whose text contains a known color keyword. */
(function () {
  var COLOR_MAP = {
    black: '#0d0d0d', white: '#f7f7f7', ivory: '#fffff0', cream: '#fffdd0',
    gray: '#808080', grey: '#808080', silver: '#c0c0c0', platinum: '#e5e4e2',
    gold: '#d4af37', 'rose gold': '#b76e79', 'rose-gold': '#b76e79', copper: '#b87333',
    bronze: '#cd7f32', brass: '#b5a642', pewter: '#8e8e8e',
    red: '#e11d30', crimson: '#dc143c', burgundy: '#800020', wine: '#722f37',
    pink: '#ffc0cb', 'hot pink': '#ff69b4', fuchsia: '#ff00ff', magenta: '#ff00ff',
    orange: '#ff7f00', peach: '#ffcba4', coral: '#ff7f50',
    yellow: '#ffd700', mustard: '#ffdb58', cream: '#fffdd0',
    green: '#2e8b57', emerald: '#50c878', olive: '#808000', sage: '#9caf88',
    blue: '#1e40af', navy: '#001f3f', teal: '#008080', turquoise: '#40e0d0', cyan: '#00ffff', sky: '#87ceeb', 'sky blue': '#87ceeb',
    purple: '#6a0dad', violet: '#8f00ff', lavender: '#e6e6fa',
    brown: '#8b4513', chocolate: '#3b1f0e', tan: '#d2b48c', beige: '#f5f5dc', khaki: '#c3b091',
    multi: 'linear-gradient(135deg,#e11d30,#d4af37,#1e40af,#2e8b57)', rainbow: 'linear-gradient(135deg,#e11d30,#d4af37,#1e40af,#2e8b57)',
    transparent: 'linear-gradient(135deg,#fff 25%,#ddd 25%,#ddd 50%,#fff 50%,#fff 75%,#ddd 75%)'
  };
  var COLOR_KEYS = Object.keys(COLOR_MAP).sort(function (a, b) { return b.length - a.length; });
  var LABEL_RE = /colou?r|finish|shade|metal|tone|material/i;

  function tryEnhance() {
    var legends = document.querySelectorAll('.product-form__input--pill legend, .product-form__input--swatch legend');
    if (!legends.length) return;
    legends.forEach(function (legend) {
      if (!LABEL_RE.test(legend.textContent || '')) return;
      var fieldset = legend.closest('fieldset');
      if (!fieldset || fieldset.dataset.v1Dotted) return;
      fieldset.dataset.v1Dotted = '1';
      var labels = fieldset.querySelectorAll('label');
      labels.forEach(function (lbl) {
        if (lbl.querySelector('.v1-color-dot')) return;
        var text = (lbl.textContent || '').toLowerCase().trim();
        for (var i = 0; i < COLOR_KEYS.length; i++) {
          var key = COLOR_KEYS[i];
          if (text.indexOf(key) !== -1) {
            var dot = document.createElement('span');
            dot.className = 'v1-color-dot';
            dot.setAttribute('aria-hidden', 'true');
            var v = COLOR_MAP[key];
            if (v.indexOf('gradient') !== -1) { dot.style.backgroundImage = v; dot.style.backgroundSize = '6px 6px'; }
            else { dot.style.backgroundColor = v; }
            lbl.insertBefore(dot, lbl.firstChild);
            break;
          }
        }
      });
    });
  }

  function schedule() {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tryEnhance);
    else tryEnhance();
    var variantSelects = document.querySelector('variant-selects');
    if (variantSelects && 'MutationObserver' in window) {
      new MutationObserver(function () { tryEnhance(); }).observe(variantSelects, { childList: true, subtree: true });
    }
  }
  schedule();
})();

/* ============================================================
   WAVE 1 STEP 8 · Predictive search — keyboard nav + ARIA
   Enhances the existing .vsps predictive dropdown with:
   - ArrowDown / ArrowUp to move highlight through results
   - Enter to activate highlighted link
   - Escape to close and return focus to input
   - ARIA combobox/listbox semantics
   ============================================================ */
(function () {
  var SEARCH_INPUTS = '.valino-trust-bar__search-input, .vmn__dr-sf2 input[type="search"]';
  var DROP_SEL = '.vsps';
  var ITEM_SEL = '.vsps__item, .vsps__all';

  function setupAria(inp) {
    if (inp.dataset.v1SearchA11y === '1') return;
    inp.dataset.v1SearchA11y = '1';
    inp.setAttribute('role', 'combobox');
    inp.setAttribute('aria-autocomplete', 'list');
    inp.setAttribute('aria-expanded', 'false');
    inp.setAttribute('aria-controls', 'v1-search-results');
  }

  function getDrop() { return document.querySelector(DROP_SEL); }
  function isOpen(drop) { return drop && drop.classList.contains('vsps--on'); }
  function items(drop) { return drop ? Array.prototype.slice.call(drop.querySelectorAll(ITEM_SEL)) : []; }

  function highlight(inp, list, idx) {
    list.forEach(function (el, i) {
      if (i === idx) {
        el.setAttribute('aria-selected', 'true');
        el.classList.add('vsps__item--active');
      } else {
        el.removeAttribute('aria-selected');
        el.classList.remove('vsps__item--active');
      }
    });
    /* Names the virtually-focused option for assistive tech without moving
       real DOM focus off the input (combobox/listbox pattern). */
    if (idx >= 0 && list[idx] && list[idx].id) {
      inp.setAttribute('aria-activedescendant', list[idx].id);
    } else {
      inp.removeAttribute('aria-activedescendant');
    }
  }

  function handleKey(e) {
    var inp = e.currentTarget;
    var drop = getDrop();
    if (!inp || !drop) return;
    /* aria-expanded is owned by show()/hide() in valino-mega-nav.liquid,
       which know the true open/close moment regardless of what triggered
       it (typing, focus, Escape, outside click). Nothing to set here. */
    if (!isOpen(drop)) return;
    var list = items(drop);
    if (!list.length) return;
    var current = list.findIndex(function (el) { return el.classList.contains('vsps__item--active'); });
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = current + 1 >= list.length ? 0 : current + 1;
      highlight(inp, list, next);
      list[next].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var prev = current <= 0 ? list.length - 1 : current - 1;
      highlight(inp, list, prev);
      list[prev].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      if (current >= 0) {
        e.preventDefault();
        list[current].click();
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      highlight(inp, list, 0);
      list[0].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'End') {
      e.preventDefault();
      highlight(inp, list, list.length - 1);
      list[list.length - 1].scrollIntoView({ block: 'nearest' });
    }
  }

  function clearHighlight(e) {
    var drop = getDrop();
    if (drop) items(drop).forEach(function (el) {
      el.removeAttribute('aria-selected');
      el.classList.remove('vsps__item--active');
    });
    var inp = e && e.currentTarget;
    if (inp) inp.removeAttribute('aria-activedescendant');
  }

  function attach() {
    var inputs = document.querySelectorAll(SEARCH_INPUTS);
    inputs.forEach(function (inp) {
      if (inp.dataset.v1SearchKey === '1') return;
      inp.dataset.v1SearchKey = '1';
      setupAria(inp);
      inp.addEventListener('keydown', handleKey);
      inp.addEventListener('input', clearHighlight);
    });
    var drop = getDrop();
    if (drop && !drop.id) {
      drop.id = 'v1-search-results';
      drop.setAttribute('role', 'listbox');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
  /* Drop is created lazily at body append; re-attach after first focus to catch it */
  document.addEventListener('focusin', function (e) {
    if (e.target && e.target.matches && e.target.matches(SEARCH_INPUTS)) {
      setTimeout(attach, 0);
    }
  });
})();

/* ============================================================
   WAVE 1 STEP 10 · Country / currency selector
   Toggles .v1-loc__panel, submits Shopify 'localization' form on select.
   ============================================================ */
(function () {
  function init() {
    var root = document.querySelector('.v1-loc');
    if (!root) return;
    var trigger = root.querySelector('.v1-loc__trigger');
    var panel = root.querySelector('.v1-loc__panel');
    var form = root.querySelector('#v1-loc-form');
    var hidden = form && form.querySelector('input[name="country_code"]');
    if (!trigger || !panel || !form || !hidden) return;

    function open() {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    }
    function close() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    }
    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (panel.hidden) open(); else close();
    });
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) { close(); trigger.focus(); }
    });
    panel.addEventListener('click', function (e) {
      var item = e.target.closest('[data-v1-country]');
      if (!item) return;
      var code = item.getAttribute('data-v1-country');
      if (!code || code === hidden.value) { close(); return; }
      hidden.value = code;
      form.submit();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 2 STEP 2 · Mobile sticky Add-to-cart bar
   Shows when main product form's submit button leaves viewport.
   Reuses existing product form to avoid duplicate cart logic.
   ============================================================ */
(function () {
  function init() {
    var bar = document.getElementById('v2-satc');
    if (!bar) return;
    var formId = bar.getAttribute('data-form-id');
    if (!formId) return;
    var form = document.getElementById(formId);
    if (!form) return;
    var mainBtn = form.querySelector('[name="add"], button[type="submit"]');
    if (!mainBtn) return;
    var stickyBtn = bar.querySelector('[data-v2="satc-add"]');
    var priceEl = bar.querySelector('[data-v2="satc-price"]');
    var cmpEl = bar.querySelector('[data-v2="satc-cmp"]');

    function show() { bar.hidden = false; bar.setAttribute('aria-hidden', 'false'); requestAnimationFrame(function () { bar.classList.add('v2-satc--on'); }); }
    function hide() { bar.classList.remove('v2-satc--on'); bar.setAttribute('aria-hidden', 'true'); setTimeout(function () { if (!bar.classList.contains('v2-satc--on')) bar.hidden = true; }, 260); }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) hide(); else show();
        });
      }, { rootMargin: '0px 0px -20% 0px' });
      io.observe(mainBtn);
    } else {
      show();
    }

    stickyBtn.addEventListener('click', function () {
      if (mainBtn.disabled) return;
      mainBtn.click();
    });

    /* Sync price + disabled state when Dawn swaps the buy button on variant change */
    function sync() {
      var dawnPrice = document.querySelector('.price__container .price-item--sale, .price__container .price-item--regular');
      if (dawnPrice && priceEl) priceEl.textContent = dawnPrice.textContent.trim();
      var dawnCmp = document.querySelector('.price__container .price-item--regular.price__sale, .price__container s.price-item');
      if (dawnCmp && cmpEl) cmpEl.textContent = dawnCmp.textContent.trim();
      stickyBtn.disabled = !!mainBtn.disabled;
      stickyBtn.textContent = mainBtn.disabled ? 'Sold out' : 'Add to cart';
    }
    sync();
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 2 STEP 3 · Wishlist (localStorage + header panel)
   Heart buttons toggle handles in storage. Header icon shows count
   and opens a panel listing saved products (hydrated via /products/X.js).
   ============================================================ */
(function () {
  var KEY = 'valino_wl_v1';

  function read() {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]') || []; }
    catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    notify();
  }
  function has(h) { return read().indexOf(h) !== -1; }
  function toggle(h) {
    var list = read();
    var idx = list.indexOf(h);
    if (idx === -1) list.unshift(h);
    else list.splice(idx, 1);
    write(list);
  }

  function syncHearts() {
    var set = read();
    document.querySelectorAll('[data-v2-wl]').forEach(function (btn) {
      var h = btn.getAttribute('data-handle');
      var on = set.indexOf(h) !== -1;
      var pressed = on ? 'true' : 'false';
      var aria = on ? 'Remove from wishlist' : 'Save to wishlist';
      if (btn.getAttribute('aria-pressed') !== pressed) btn.setAttribute('aria-pressed', pressed);
      if (btn.getAttribute('aria-label') !== aria) btn.setAttribute('aria-label', aria);
      var lbl = btn.querySelector('[data-v2-wl-lbl]');
      var want = on ? 'Saved' : 'Save';
      if (lbl && lbl.textContent !== want) lbl.textContent = want;
    });
  }
  function syncCount() {
    var n = read().length;
    document.querySelectorAll('[data-v2-wl-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }
  function notify() { syncHearts(); syncCount(); }

  function bindHearts(root) {
    (root || document).querySelectorAll('[data-v2-wl]').forEach(function (btn) {
      if (btn.dataset.v2WlBound === '1') return;
      btn.dataset.v2WlBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var h = btn.getAttribute('data-handle');
        if (!h) return;
        toggle(h);
      });
    });
  }

  /* Header panel */
  function buildPanel() {
    var panel = document.getElementById('v2-wl-panel');
    if (panel) return panel;
    panel = document.createElement('div');
    panel.id = 'v2-wl-panel';
    panel.className = 'v2-wl-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Wishlist');
    panel.hidden = true;
    panel.innerHTML =
      '<div class="v2-wl-panel__head"><span>Wishlist</span>'
    + '<button type="button" class="v2-wl-panel__close" aria-label="Close" data-v2-wl-close>&times;</button></div>'
    + '<ul class="v2-wl-panel__list" data-v2-wl-list></ul>';
    document.body.appendChild(panel);
    panel.querySelector('[data-v2-wl-close]').addEventListener('click', close);
    document.addEventListener('click', function (e) {
      if (panel.hidden) return;
      var trigger = document.getElementById('v2-wl-header');
      if (panel.contains(e.target) || (trigger && trigger.contains(e.target))) return;
      close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) close();
    });
    return panel;
  }

  function money(cents) {
    var n = parseFloat(cents);
    if (isNaN(n)) return '';
    var d = n / 100;
    return '$' + (Number.isInteger(d) ? d : d.toFixed(2));
  }

  function renderPanel() {
    var panel = buildPanel();
    var list = panel.querySelector('[data-v2-wl-list]');
    var handles = read();
    if (!handles.length) {
      list.innerHTML = '<li class="v2-wl-panel__empty">Your wishlist is empty. Tap the heart on any product to save it.</li>';
      return;
    }
    list.innerHTML = '<li class="v2-wl-panel__empty">Loading…</li>';
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var html = products.map(function (p, i) {
        if (!p) return '';
        var img = p.featured_image || (p.images && p.images[0]) || '';
        if (img && typeof img !== 'string') img = img.src || img.url || '';
        if (img && img.indexOf('//') === 0) img = 'https:' + img;
        if (img) img = img.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master)\./, '_96x96.');
        var price = p.price || (p.variants && p.variants[0] && p.variants[0].price) || 0;
        return '<li class="v2-wl-panel__item-wrap">'
          + '<a href="' + p.url + '" class="v2-wl-panel__item">'
          + (img ? '<img class="v2-wl-panel__img" src="' + img + '" alt="" loading="lazy">' : '<div class="v2-wl-panel__img"></div>')
          + '<div class="v2-wl-panel__meta"><p class="v2-wl-panel__name">' + (p.title || '') + '</p>'
          + '<span class="v2-wl-panel__price">' + money(price) + '</span></div>'
          + '<button type="button" class="v2-wl-panel__remove" data-v2-wl-rm="' + handles[i] + '" aria-label="Remove">&times;</button>'
          + '</a></li>';
      }).join('');
      list.innerHTML = html || '<li class="v2-wl-panel__empty">Your wishlist is empty.</li>';
      list.querySelectorAll('[data-v2-wl-rm]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          toggle(btn.getAttribute('data-v2-wl-rm'));
          renderPanel();
        });
      });
    });
  }

  function open() {
    var panel = buildPanel();
    panel.hidden = false;
    var trigger = document.getElementById('v2-wl-header');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    renderPanel();
  }
  function close() {
    var panel = document.getElementById('v2-wl-panel');
    if (panel) panel.hidden = true;
    var trigger = document.getElementById('v2-wl-header');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function init() {
    bindHearts();
    syncHearts();
    syncCount();
    var trigger = document.getElementById('v2-wl-header');
    if (trigger) trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var panel = document.getElementById('v2-wl-panel');
      if (panel && !panel.hidden) close(); else open();
    });
    /* Re-bind on dynamic DOM (card swap, variant change etc.)
       IMPORTANT: syncHearts() mutates textContent on wishlist labels, which
       itself fires childList mutations. Only resync when wishlist buttons
       were actually added/removed, otherwise this observer infinite-loops
       and freezes the main thread (Page Unresponsive on PDPs). */
    if ('MutationObserver' in window) {
      /* Coalesce syncs to rAF — broad subtree observers on body fire for every
         DOM change in the app (image loads, variant swaps, cart updates); doing
         work synchronously inside the callback is a classic scroll-jank cause. */
      var wlPending = false;
      new MutationObserver(function (muts) {
        if (wlPending) return;
        var needsSync = false;
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          if (!added || !added.length) continue;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if ((n.hasAttribute && n.hasAttribute('data-v2-wl')) ||
                (n.querySelector && n.querySelector('[data-v2-wl]'))) {
              bindHearts(n);
              needsSync = true;
            }
          }
        }
        if (!needsSync) return;
        wlPending = true;
        requestAnimationFrame(function () {
          wlPending = false;
          syncHearts();
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
    /* Cross-tab sync */
    window.addEventListener('storage', function (e) { if (e.key === KEY) notify(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 2 STEP 4 · Review dialog open/close
   ============================================================ */
(function () {
  function init() {
    var dlg = document.querySelector('[data-v2-rev-dlg]');
    var openBtn = document.querySelector('[data-v2-rev-open]');
    if (!dlg || !openBtn || !dlg.showModal) return;
    openBtn.addEventListener('click', function () { dlg.showModal(); });
    /* Re-open if form was submitted with errors or successfully (so user sees the message) */
    if (dlg.querySelector('.v2-rev__ok, .v2-rev__err')) dlg.showModal();
    /* Close buttons (any element with data-v2-rev-close) */
    dlg.querySelectorAll('[data-v2-rev-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { dlg.close(); });
    });
    /* Click outside dialog content to close */
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) dlg.close();
    });
    /* Star rating hint text */
    var HINTS = ['Tap to rate', 'Disappointing', 'Just okay', 'Good', 'Love it', 'Extraordinary'];
    var hint = dlg.querySelector('[data-v2-rev-hint]');
    var stars = dlg.querySelectorAll('[data-v2-rev-star]');
    var rateLabels = dlg.querySelectorAll('.v2-rev__rate label');
    function showHint(n) { if (hint) hint.textContent = HINTS[n] || HINTS[0]; }
    stars.forEach(function (s) {
      s.addEventListener('change', function () { showHint(parseInt(s.value, 10) || 0); });
    });
    rateLabels.forEach(function (lbl) {
      lbl.addEventListener('mouseenter', function () {
        var n = parseInt(lbl.getAttribute('data-stars'), 10) || 0;
        showHint(n);
      });
    });
    var rateWrap = dlg.querySelector('.v2-rev__rate');
    if (rateWrap) rateWrap.addEventListener('mouseleave', function () {
      var checked = dlg.querySelector('[data-v2-rev-star]:checked');
      showHint(checked ? (parseInt(checked.value, 10) || 0) : 0);
    });
    /* Character counter */
    var body = dlg.querySelector('[data-v2-rev-body]');
    var count = dlg.querySelector('[data-v2-rev-count]');
    if (body && count) {
      var updateCount = function () { count.textContent = body.value.length; };
      body.addEventListener('input', updateCount);
      updateCount();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 2 STEP 5 · Cart drawer upsells (complementary → related fallback)
   ============================================================ */
(function () {
  function money(cents) {
    var n = parseFloat(cents);
    if (isNaN(n)) return '';
    var d = n / 100;
    return '$' + (Number.isInteger(d) ? d : d.toFixed(2));
  }

  function card(p) {
    if (!p) return '';
    var img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && typeof img !== 'string') img = img.src || img.url || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    if (img) img = img.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master)\./, '_280x280.');
    var variantId = p.variants && p.variants[0] && p.variants[0].id;
    var price = p.price || (p.variants && p.variants[0] && p.variants[0].price) || 0;
    var imgTag = img
      ? '<img class="v2-cu__img" src="' + img + '" alt="" loading="lazy">'
      : '<div class="v2-cu__img"></div>';
    var addBtn = (p.available !== false && variantId)
      ? '<button type="button" class="v2-cu__add" data-v2-cu-add="' + variantId + '" aria-label="Add ' + (p.title || 'product') + ' to cart">+</button>'
      : '';
    return '<div class="v2-cu__card-wrap">'
      + '<a href="' + p.url + '" class="v2-cu__card">'
      + imgTag
      + '<div class="v2-cu__info"><p class="v2-cu__name">' + (p.title || '') + '</p>'
      + '<span class="v2-cu__price">' + money(price) + '</span></div>'
      + '</a>' + addBtn + '</div>';
  }

  function hydrate() {
    var root = document.getElementById('v2-cu');
    var track = document.getElementById('v2-cu-track');
    if (!root || !track) return;
    var anchor = root.getAttribute('data-anchor');
    if (!anchor) { root.hidden = true; return; }

    function load(intent) {
      return fetch('/recommendations/products.json?product_id=' + encodeURIComponent(anchor)
                 + '&limit=6&intent=' + intent)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) { return (d && d.products) ? d.products : []; })
        .catch(function () { return []; });
    }
    load('complementary').then(function (list) {
      if (!list || list.length < 3) return load('related').then(function (l2) { return l2 && l2.length >= 3 ? l2 : []; });
      return list;
    }).then(function (list) {
      if (!list || list.length < 3) { root.hidden = true; return; }
      track.innerHTML = list.slice(0, 6).map(card).join('');
      root.hidden = false;
      track.querySelectorAll('[data-v2-cu-add]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var vid = btn.getAttribute('data-v2-cu-add');
          btn.disabled = true;
          btn.textContent = '…';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
          }).then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          }).then(function () {
            /* Refresh cart drawer via Dawn's cart-notification event pattern */
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.renderContents === 'function') {
              return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
                drawer.renderContents({ sections: null, items: cart.items, item_count: cart.item_count });
              }).catch(function () { location.reload(); });
            }
            location.reload();
          }).catch(function () {
            btn.disabled = false;
            btn.textContent = '+';
          });
        });
      });
    });
  }

  /* Hydrate on load, and again whenever cart drawer opens or cart contents change */
  function watch() {
    hydrate();
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[id*="cart-icon-bubble"], a[href="/cart"], #cart-icon-bubble')) {
        setTimeout(hydrate, 250);
      }
    });
    document.addEventListener('cart:refresh', hydrate);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();

/* ============================================================
   WAVE 2 STEP 7 · Size guide dialog
   ============================================================ */
(function () {
  function init() {
    var dlg = document.querySelector('[data-v2-sg-dlg]');
    var btn = document.querySelector('[data-v2-sg-open]');
    if (!dlg || !btn || !dlg.showModal) return;
    btn.addEventListener('click', function () { dlg.showModal(); });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 2 STEP 8 · Honest delivery date estimator
   3 business days dispatch + 5–12 business days delivery
   ============================================================ */
(function () {
  var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return d;
  }
  function fmt(d) { return DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate(); }
  function sameMonth(a, b) { return a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear(); }

  function init() {
    var root = document.querySelector('[data-v2-de]');
    if (!root) return;
    var shipEl = root.querySelector('[data-v2-de-ship]');
    var rangeEl = root.querySelector('[data-v2-de-range]');
    var now = new Date();
    /* Ship-by: now + 3 business days (addBusinessDays never lands on a weekend) */
    var ship = addBusinessDays(now, 3);
    while (ship.getDay() === 0 || ship.getDay() === 6) ship.setDate(ship.getDate() + 1);
    /* Delivery window: ship + 5 to ship + 12 business days */
    var early = addBusinessDays(ship, 5);
    var late = addBusinessDays(ship, 12);
    shipEl.textContent = fmt(ship);
    if (sameMonth(early, late)) {
      rangeEl.textContent = MONTHS[early.getMonth()] + ' ' + early.getDate() + '–' + late.getDate();
    } else {
      rangeEl.textContent = MONTHS[early.getMonth()] + ' ' + early.getDate() + ' – ' + MONTHS[late.getMonth()] + ' ' + late.getDate();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 3 STEP 2 · Quick view modal (native <dialog>, lazy)
   One dialog reused across all [data-v3-qv-open] cards.
   Fetches /products/{handle}.js, renders media + variants + add-to-cart.
   ============================================================ */
(function () {
  var dlg = null, host = null, cache = {}, state = { product: null, variant: null };

  function formatMoney(cents) {
    var v = (cents / 100);
    /* Shopify JS globals (Shopify.currency) may exist; fallback to USD */
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(v); }
    catch (e) { return '$' + v.toFixed(2); }
  }

  function buildDialog() {
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = 'v3-qv-dlg';
    dlg.setAttribute('aria-label', 'Quick view');
    dlg.innerHTML =
      '<button type="button" class="v3-qv-dlg__close" data-v3-qv-close aria-label="Close">&times;</button>' +
      '<div class="v3-qv-dlg__grid" data-v3-qv-body>' +
      '  <div class="v3-qv-dlg__status">Loading…</div>' +
      '</div>';
    document.body.appendChild(dlg);
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) dlg.close();
      if (e.target.closest('[data-v3-qv-close]')) dlg.close();
    });
    dlg.addEventListener('close', function () {
      state.product = null; state.variant = null;
    });
    host = dlg.querySelector('[data-v3-qv-body]');
    return dlg;
  }

  function render(product) {
    state.product = product;
    state.variant = (product.variants || []).find(function (v) { return v.available; }) || product.variants[0];
    var img = (product.featured_image || (product.images && product.images[0])) || '';
    var desc = (product.description || '').replace(/<[^>]+>/g, '').slice(0, 220);
    var variantsHtml = '';
    if (product.variants && product.variants.length > 1) {
      variantsHtml = '<div class="v3-qv-dlg__variants" role="radiogroup" aria-label="Variant">';
      product.variants.forEach(function (v) {
        variantsHtml += '<button type="button" class="v3-qv-dlg__variant' + (v.id === state.variant.id ? ' is-selected' : '') + '"' +
          ' data-v-id="' + v.id + '"' + (v.available ? '' : ' disabled') +
          ' aria-checked="' + (v.id === state.variant.id ? 'true' : 'false') + '" role="radio">' +
          escapeHtml(v.title) + '</button>';
      });
      variantsHtml += '</div>';
    }
    var priceHtml = '<div class="v3-qv-dlg__price" data-v3-qv-price>' + formatMoney(state.variant.price);
    if (state.variant.compare_at_price && state.variant.compare_at_price > state.variant.price) {
      priceHtml += '<span class="v3-qv-dlg__cmp">' + formatMoney(state.variant.compare_at_price) + '</span>';
    }
    priceHtml += '</div>';

    host.innerHTML =
      '<div class="v3-qv-dlg__media">' +
      (img ? '<img class="v3-qv-dlg__img" src="' + img + '" alt="' + escapeHtml(product.title) + '" loading="lazy" />' : '') +
      '</div>' +
      '<div class="v3-qv-dlg__info">' +
      (product.vendor ? '<div class="v3-qv-dlg__vendor">' + escapeHtml(product.vendor) + '</div>' : '') +
      '<h2 class="v3-qv-dlg__title">' + escapeHtml(product.title) + '</h2>' +
      priceHtml +
      (desc ? '<p class="v3-qv-dlg__desc">' + escapeHtml(desc) + (product.description && product.description.length > 220 ? '…' : '') + '</p>' : '') +
      variantsHtml +
      '<div class="v3-qv-dlg__actions">' +
      '<button type="button" class="v3-qv-dlg__add" data-v3-qv-add' + (state.variant.available ? '' : ' disabled') + '>' +
      (state.variant.available ? 'Add to cart' : 'Sold out') + '</button>' +
      '<a class="v3-qv-dlg__view" href="' + product.url + '">View full details</a>' +
      '</div>' +
      '</div>';
  }

  function updatePrice() {
    var priceEl = host.querySelector('[data-v3-qv-price]');
    if (!priceEl || !state.variant) return;
    var html = formatMoney(state.variant.price);
    if (state.variant.compare_at_price && state.variant.compare_at_price > state.variant.price) {
      html += '<span class="v3-qv-dlg__cmp">' + formatMoney(state.variant.compare_at_price) + '</span>';
    }
    priceEl.innerHTML = html;
    var addBtn = host.querySelector('[data-v3-qv-add]');
    if (addBtn) {
      addBtn.disabled = !state.variant.available;
      addBtn.textContent = state.variant.available ? 'Add to cart' : 'Sold out';
    }
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function openHandle(handle) {
    buildDialog();
    host.innerHTML = '<div class="v3-qv-dlg__status">Loading…</div>';
    if (!dlg.open) dlg.showModal();
    var load = cache[handle]
      ? Promise.resolve(cache[handle])
      : fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('fetch'); return r.json(); })
          .then(function (p) { cache[handle] = p; return p; });
    load.then(render).catch(function () {
      host.innerHTML = '<div class="v3-qv-dlg__status">Unable to load product. <a href="/products/' + encodeURIComponent(handle) + '">Open full page</a>.</div>';
    });
  }

  function onClick(e) {
    var trigger = e.target.closest('[data-v3-qv-open]');
    if (trigger) {
      e.preventDefault();
      var handle = trigger.getAttribute('data-handle');
      if (handle) openHandle(handle);
      return;
    }
    if (!dlg || !dlg.open) return;
    var vBtn = e.target.closest('[data-v-id]');
    if (vBtn && state.product) {
      var id = Number(vBtn.getAttribute('data-v-id'));
      var v = state.product.variants.find(function (x) { return x.id === id; });
      if (!v) return;
      state.variant = v;
      host.querySelectorAll('[data-v-id]').forEach(function (b) {
        var sel = Number(b.getAttribute('data-v-id')) === id;
        b.classList.toggle('is-selected', sel);
        b.setAttribute('aria-checked', sel ? 'true' : 'false');
      });
      updatePrice();
      return;
    }
    var add = e.target.closest('[data-v3-qv-add]');
    if (add && state.variant) {
      add.disabled = true; add.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: state.variant.id, quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
        .then(function () {
          add.textContent = 'Added ✓';
          /* Refresh cart drawer if present */
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            fetch('/cart.js', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                drawer.renderContents({ sections: null, items: cart.items, item_count: cart.item_count });
                if (typeof drawer.open === 'function') drawer.open(add);
              }).catch(function () {});
          }
          setTimeout(function () {
            if (dlg.open) dlg.close();
            add.disabled = false; add.textContent = 'Add to cart';
          }, 900);
        })
        .catch(function () {
          add.textContent = 'Could not add — try again';
          setTimeout(function () { add.disabled = false; add.textContent = 'Add to cart'; }, 1500);
        });
    }
  }

  function init() {
    document.addEventListener('click', onClick);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 3 STEP 3 · Product compare (up to 4, localStorage)
   Toggle [data-v3-cmp-toggle] adds/removes; floating bar shows count.
   "Compare" button opens side-by-side modal (image, price, vendor, description).
   ============================================================ */
(function () {
  var KEY = 'valino_compare_v1';
  var MAX = 4;
  var bar = null, dlg = null, productCache = {};

  function read() {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
  }
  function write(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr.slice(0, MAX))); } catch (e) {}
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function fmtMoney(cents) {
    var v = cents / 100;
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(v); }
    catch (e) { return '$' + v.toFixed(2); }
  }

  function buildBar() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'v3-cmp-bar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Compare products');
    bar.innerHTML =
      '<div class="v3-cmp-bar__list" data-v3-cmp-list></div>' +
      '<span class="v3-cmp-bar__count" data-v3-cmp-count></span>' +
      '<button type="button" class="v3-cmp-bar__go" data-v3-cmp-open>Compare</button>' +
      '<button type="button" class="v3-cmp-bar__clear" data-v3-cmp-clear aria-label="Clear compare list">Clear</button>';
    document.body.appendChild(bar);
    return bar;
  }

  function renderBar() {
    var items = read();
    if (!items.length) {
      if (bar) bar.classList.remove('is-visible');
      return;
    }
    buildBar();
    var list = bar.querySelector('[data-v3-cmp-list]');
    var count = bar.querySelector('[data-v3-cmp-count]');
    var go = bar.querySelector('[data-v3-cmp-open]');
    list.innerHTML = items.map(function (p) {
      return '<div class="v3-cmp-bar__thumb" title="' + escapeHtml(p.title) + '">' +
        (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" />' : '') +
        '<button type="button" class="v3-cmp-bar__thumb-close" data-v3-cmp-remove="' + escapeHtml(p.handle) + '" aria-label="Remove ' + escapeHtml(p.title) + ' from compare">×</button>' +
        '</div>';
    }).join('');
    count.textContent = items.length + ' item' + (items.length > 1 ? 's' : '') + ' to compare';
    go.disabled = items.length < 2;
    bar.classList.add('is-visible');
  }

  function syncButtons() {
    var items = read();
    var handles = items.map(function (i) { return i.handle; });
    document.querySelectorAll('[data-v3-cmp-toggle]').forEach(function (btn) {
      var on = handles.indexOf(btn.getAttribute('data-handle')) !== -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var lbl = btn.querySelector('.v3-cmp-btn__lbl');
      if (lbl) lbl.textContent = on ? 'Added' : 'Compare';
    });
  }

  function toggle(btn) {
    var handle = btn.getAttribute('data-handle');
    if (!handle) return;
    var items = read();
    var idx = -1;
    for (var i = 0; i < items.length; i++) { if (items[i].handle === handle) { idx = i; break; } }
    if (idx !== -1) {
      items.splice(idx, 1);
    } else {
      if (items.length >= MAX) {
        flashMessage('Compare is full — remove a product first (max ' + MAX + ').');
        return;
      }
      items.push({
        handle: handle,
        title: btn.getAttribute('data-title') || handle,
        image: btn.getAttribute('data-image') || '',
        url: btn.getAttribute('data-url') || ('/products/' + handle),
        price: btn.getAttribute('data-price') || '',
        vendor: btn.getAttribute('data-vendor') || ''
      });
    }
    write(items);
    syncButtons();
    renderBar();
  }

  function flashMessage(msg) {
    var n = document.createElement('div');
    n.textContent = msg;
    n.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#0d0d0d;color:#fff;padding:10px 18px;border-radius:999px;font-size:13px;z-index:1100;box-shadow:0 10px 28px rgba(0,0,0,0.3);';
    document.body.appendChild(n);
    setTimeout(function () { n.remove(); }, 2400);
  }

  function removeHandle(handle) {
    var items = read().filter(function (i) { return i.handle !== handle; });
    write(items);
    syncButtons();
    renderBar();
    if (dlg && dlg.open) renderDialog();
  }

  function clearAll() {
    write([]);
    syncButtons();
    if (bar) bar.classList.remove('is-visible');
    if (dlg && dlg.open) dlg.close();
  }

  function buildDialog() {
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = 'v3-cmp-dlg';
    dlg.setAttribute('aria-label', 'Compare products');
    dlg.innerHTML =
      '<button type="button" class="v3-cmp-dlg__close" data-v3-cmp-close aria-label="Close">&times;</button>' +
      '<div class="v3-cmp-dlg__body" data-v3-cmp-body></div>';
    document.body.appendChild(dlg);
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) dlg.close();
      if (e.target.closest('[data-v3-cmp-close]')) dlg.close();
    });
    return dlg;
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('fetch'); return r.json(); })
      .then(function (p) { productCache[handle] = p; return p; });
  }

  function renderDialog() {
    var items = read();
    var body = dlg.querySelector('[data-v3-cmp-body]');
    if (!items.length) {
      body.innerHTML = '<h2 class="v3-cmp-dlg__title">Compare products</h2><p>Nothing to compare yet. Tap “Compare” on product cards to add items.</p>';
      return;
    }
    body.innerHTML = '<h2 class="v3-cmp-dlg__title">Compare products</h2><p>Loading…</p>';

    Promise.all(items.map(function (i) { return fetchProduct(i.handle).catch(function () { return null; }); }))
      .then(function (products) {
        var valid = [];
        products.forEach(function (p, i) { if (p) valid.push({ p: p, meta: items[i] }); });
        if (!valid.length) {
          body.innerHTML = '<h2 class="v3-cmp-dlg__title">Compare products</h2><p>Unable to load products.</p>';
          return;
        }
        var head = '<tr class="v3-cmp-tbl__head"><th></th>' + valid.map(function (x) {
          var p = x.p;
          var img = p.featured_image || (p.images && p.images[0]) || '';
          return '<td>' +
            (img ? '<img class="v3-cmp-tbl__img" src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" />' : '') +
            '<a class="v3-cmp-tbl__name" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>' +
            '<button type="button" class="v3-cmp-tbl__remove" data-v3-cmp-remove="' + escapeHtml(p.handle) + '">Remove</button>' +
            '</td>';
        }).join('') + '</tr>';

        var priceRow = '<tr><th>Price</th>' + valid.map(function (x) {
          var p = x.p;
          var html = '<span class="v3-cmp-tbl__price">' + fmtMoney(p.price) + '</span>';
          if (p.compare_at_price && p.compare_at_price > p.price) {
            html += ' <span style="color:#aaa;text-decoration:line-through;font-size:12px;">' + fmtMoney(p.compare_at_price) + '</span>';
          }
          return '<td>' + html + '</td>';
        }).join('') + '</tr>';

        var vendorRow = '<tr><th>Brand</th>' + valid.map(function (x) {
          return '<td>' + escapeHtml(x.p.vendor || '—') + '</td>';
        }).join('') + '</tr>';

        var typeRow = '<tr><th>Category</th>' + valid.map(function (x) {
          return '<td>' + escapeHtml(x.p.type || '—') + '</td>';
        }).join('') + '</tr>';

        var availRow = '<tr><th>Stock</th>' + valid.map(function (x) {
          return '<td>' + (x.p.available ? 'In stock' : 'Sold out') + '</td>';
        }).join('') + '</tr>';

        var variantRow = '<tr><th>Options</th>' + valid.map(function (x) {
          var count = (x.p.variants || []).length;
          return '<td>' + (count > 1 ? (count + ' variants') : 'One option') + '</td>';
        }).join('') + '</tr>';

        var descRow = '<tr><th>Description</th>' + valid.map(function (x) {
          var d = (x.p.description || '').replace(/<[^>]+>/g, '').trim();
          if (d.length > 180) d = d.slice(0, 180) + '…';
          return '<td>' + escapeHtml(d || '—') + '</td>';
        }).join('') + '</tr>';

        var actionRow = '<tr><th></th>' + valid.map(function (x) {
          var p = x.p;
          var firstAvail = (p.variants || []).find(function (v) { return v.available; }) || p.variants[0];
          var vid = firstAvail ? firstAvail.id : '';
          return '<td><button type="button" class="v3-cmp-tbl__add" data-v3-cmp-add="' + vid + '"' +
            (p.available ? '' : ' disabled') + '>' + (p.available ? 'Add to cart' : 'Sold out') + '</button></td>';
        }).join('') + '</tr>';

        body.innerHTML =
          '<h2 class="v3-cmp-dlg__title">Compare products</h2>' +
          '<table class="v3-cmp-tbl">' +
          head + priceRow + vendorRow + typeRow + availRow + variantRow + descRow + actionRow +
          '</table>';
      });
  }

  function onClick(e) {
    var tg = e.target.closest('[data-v3-cmp-toggle]');
    if (tg) { e.preventDefault(); toggle(tg); return; }
    var rm = e.target.closest('[data-v3-cmp-remove]');
    if (rm) { e.preventDefault(); removeHandle(rm.getAttribute('data-v3-cmp-remove')); return; }
    var clr = e.target.closest('[data-v3-cmp-clear]');
    if (clr) { e.preventDefault(); clearAll(); return; }
    var op = e.target.closest('[data-v3-cmp-open]');
    if (op) {
      e.preventDefault();
      buildDialog();
      if (!dlg.open) dlg.showModal();
      renderDialog();
      return;
    }
    var add = e.target.closest('[data-v3-cmp-add]');
    if (add && !add.disabled) {
      var vid = Number(add.getAttribute('data-v3-cmp-add'));
      if (!vid) return;
      add.disabled = true; var orig = add.textContent; add.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
      }).then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
        .then(function () {
          add.textContent = 'Added ✓';
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            fetch('/cart.js', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                drawer.renderContents({ sections: null, items: cart.items, item_count: cart.item_count });
              }).catch(function () {});
          }
          setTimeout(function () { add.disabled = false; add.textContent = orig; }, 1200);
        }).catch(function () {
          add.textContent = 'Could not add';
          setTimeout(function () { add.disabled = false; add.textContent = orig; }, 1500);
        });
    }
  }

  function init() {
    syncButtons();
    renderBar();
    document.addEventListener('click', onClick);
    /* Sync across tabs */
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) { syncButtons(); renderBar(); if (dlg && dlg.open) renderDialog(); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   WAVE 3 STEP 7 · Account Reorder button
   Reads line items from data-v3-reorder (JSON array), POSTs to /cart/add.js,
   redirects to /cart on success.
   ============================================================ */
(function () {
  function onClick(e) {
    var btn = e.target.closest('[data-v3-reorder]');
    if (!btn) return;
    e.preventDefault();
    var raw = btn.getAttribute('data-v3-reorder');
    var items;
    try { items = JSON.parse(raw); } catch (err) { return; }
    if (!Array.isArray(items) || !items.length) return;
    var origText = btn.textContent.trim();
    btn.disabled = true; btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('add');
      return r.json();
    }).then(function () {
      btn.classList.add('is-success');
      btn.textContent = 'Added · Go to cart →';
      setTimeout(function () { window.location.href = '/cart'; }, 700);
    }).catch(function () {
      btn.classList.add('is-error');
      btn.textContent = 'Items unavailable';
      setTimeout(function () {
        btn.disabled = false;
        btn.classList.remove('is-error');
        btn.textContent = origText;
      }, 2200);
    });
  }
  document.addEventListener('click', onClick);
})();

/* v2-rev-redesign-1776652609 */
